import { useEffect, useMemo, useRef } from 'react';
import { CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { getParsedEnrollmentFormFields } from '../utils/applicant-info-fields';
import { EnrollmentFormState } from '../widgets/care-credit-enrollment-multi-step/enrollment-multi-step.types';

enum Application_Types {
  Print_Full_Application = 'Print_Full_Application',
  Signature_page = 'Signature_page',
}

export type UseCareCreditPrefillApplicationQueriesProps = {
  locationId: string | undefined;
  enrollmentFormData: EnrollmentFormState;
};
export const useCareCreditPrefillApplicationQueries = ({
  locationId,
  enrollmentFormData,
}: UseCareCreditPrefillApplicationQueriesProps) => {
  const mounted = useRef(false);

  const applicantInfo = useMemo(() => {
    const {
      // personal info
      firstName,
      lastName,
      email,
      dateOfBirth,
      annualIncomeNumber,

      // contact info
      phoneNumber,
      // phoneNumberType,

      // address
      address,
      address2,
      city,
      state,
      postalCode,
    } = getParsedEnrollmentFormFields(enrollmentFormData.fields);

    const ssn = enrollmentFormData.fields.ssn;

    return {
      firstName,
      lastName,
      annualIncome: annualIncomeNumber,
      emailAddress: email,
      dateOfBirth,
      ssn,
      contact: [{ contactType: 'HOME', contactNumber: phoneNumber }],
      address: {
        addressLine1: address,
        addressLine2: address2,
        city,
        state,
        zipCode: postalCode,
      },
    };
  }, [enrollmentFormData.fields]);

  const {
    data: fullPageApplication,
    isLoading: isLoadingFullPageApplication,
    refetch: refetchFullPageApplication,
  } = CareCreditIntegrationV1.Queries.usePrefillDataApplicationQuery({
    request: {
      type: Application_Types.Print_Full_Application,
      locationId,
      applicantInfo,
    },
    options: {
      enabled: !!locationId && mounted.current,
      retry: 0,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: signaturePage,
    isLoading: isLoadingSignaturePage,
    refetch: refetchSignaturePage,
  } = CareCreditIntegrationV1.Queries.usePrefillDataApplicationQuery({
    request: {
      type: Application_Types.Signature_page,
      locationId,
      applicantInfo,
    },
    options: {
      enabled: !!locationId && mounted.current,
      retry: 0,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!mounted.current) {
        mounted.current = true;
        refetchFullPageApplication();
        refetchSignaturePage();
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      mounted.current = false;
    };
  }, []);

  return {
    fullPageApplication,
    isLoadingFullPageApplication,
    signaturePage,
    isLoadingSignaturePage,
  };
};
